import { Id } from '@/convex/_generated/dataModel';
import {
	ENGAGEMENT_POD_CAPABILITIES,
	ENGAGEMENT_POD_PLATFORM_ORDER,
	explainEngagementPodModeBlock,
	getEngagementPodCapability,
	getEngagementPodModeState,
	normalizeEngagementPodPlatform,
	type EngagementPodMode,
	type EngagementPodPlatform,
} from '@shared/engagementPodCapabilities';

export type Platform = EngagementPodPlatform;

export type PodMode = EngagementPodMode;

export type PodCapabilityStatus = 'supported' | 'disabled' | 'blocked';

export interface PodModeCapability {
	enabled: boolean;
	status: PodCapabilityStatus;
	reason?: string;
	urlKind?: 'profile' | 'post' | 'none';
	placeholder?: string;
	helperText?: string;
}

export interface PodPlatformCapability {
	platform: Platform;
	label: string;
	shortLabel?: string;
	icon: string;
	aliases: string[];
	visibleInCreate?: boolean;
	providerFields?: string[];
	modes: Record<PodMode, PodModeCapability>;
}

const toModeCapability = (
	platform: EngagementPodPlatform,
	mode: PodMode
): PodModeCapability => {
	const state = getEngagementPodModeState(platform, mode);
	return {
		enabled: state === 'enabled',
		status: state === 'enabled' ? 'supported' : state,
		reason: explainEngagementPodModeBlock(platform, mode) || undefined,
		urlKind: state === 'enabled' ? (mode === 'broadcast' ? 'profile' : 'post') : 'none',
		placeholder:
			platform === 'twitter'
				? mode === 'broadcast'
					? 'https://x.com/username'
					: 'https://x.com/username/status/123456'
				: undefined,
		helperText:
			platform === 'twitter'
				? mode === 'broadcast'
					? 'Add X profile URLs for Broadcast source collection.'
					: 'Add X post URLs. External Swarm replies are X-only in this implementation.'
				: undefined,
	};
};

const toPodPlatformCapability = (
	platform: EngagementPodPlatform
): PodPlatformCapability => {
	const capability = ENGAGEMENT_POD_CAPABILITIES[platform];
	return {
		platform,
		label: capability.label,
		shortLabel: capability.shortLabel,
		icon: capability.icon,
		aliases:
			platform === 'twitter'
				? ['x', 'twitter', 'twitterx', 'xtwitter']
				: [platform],
		visibleInCreate: platform !== 'tiktok',
		providerFields: capability.requiredFields,
		modes: {
			broadcast: toModeCapability(platform, 'broadcast'),
			swarm: toModeCapability(platform, 'swarm'),
		},
	};
};

export const POD_PLATFORM_CAPABILITIES: readonly PodPlatformCapability[] =
	ENGAGEMENT_POD_PLATFORM_ORDER.map(toPodPlatformCapability);

export const POD_PLATFORM_ORDER = ENGAGEMENT_POD_PLATFORM_ORDER;

export function canonicalPodPlatform(platform?: string | null): string {
	return normalizeEngagementPodPlatform(platform || '') || String(platform || '').toLowerCase();
}

export function getPodPlatformCapability(
	platform?: string | null
): PodPlatformCapability {
	const canonical = canonicalPodPlatform(platform);
	const shared = getEngagementPodCapability(canonical);
	if (shared) return toPodPlatformCapability(shared.platform);

	const label = String(platform || 'Unknown platform');
	return {
		platform: 'twitter',
		label,
		icon: '?',
		aliases: [canonical],
		visibleInCreate: false,
		modes: {
			broadcast: {
				enabled: false,
				status: 'blocked',
				reason: `${label} Broadcast pods are not in the current Upload Post capability matrix.`,
				urlKind: 'none',
			},
			swarm: {
				enabled: false,
				status: 'blocked',
				reason: `${label} external Swarm replies are not in the current Upload Post capability matrix.`,
				urlKind: 'none',
			},
		},
	};
}

export function getPodModeCapability(
	platform: string | null | undefined,
	mode: PodMode | string | null | undefined
): PodModeCapability {
	const normalizedMode: PodMode = mode === 'broadcast' ? 'broadcast' : 'swarm';
	return getPodPlatformCapability(platform).modes[normalizedMode];
}

export function isPodModeSupported(
	platform: string | null | undefined,
	mode: PodMode | string | null | undefined
) {
	return getPodModeCapability(platform, mode).enabled;
}

export function getPodPlatformLabel(platform?: string | null) {
	const capability = getPodPlatformCapability(platform);
	return capability.shortLabel || capability.label;
}

export function getPodPlatformIcon(platform?: string | null) {
	return getPodPlatformCapability(platform).icon;
}

export function getPodPlatformOptions(
	mode: PodMode,
	options: { includeDisabled?: boolean; includeHidden?: boolean } = {}
) {
	const { includeDisabled = true, includeHidden = false } = options;
	return POD_PLATFORM_CAPABILITIES.filter((capability) => {
		if (!includeHidden && capability.visibleInCreate === false) {
			return false;
		}
		return includeDisabled || capability.modes[mode].enabled;
	}).map((capability) => ({
		...capability,
		modeCapability: capability.modes[mode],
	}));
}

export function getDefaultPodPlatform(mode: PodMode): Platform {
	return (
		getPodPlatformOptions(mode, { includeDisabled: false })[0]?.platform ||
		'twitter'
	);
}

export interface PodMember {
	email: string;
	socialUsername: string;
	/** Compatibility alias: stores the Upload Post username/profileKey in v1. */
	uploadPostProfileKey: string;
}

export type PodStatus = string;

export interface StatusUpdate {
	status: PodStatus;
	message: string;
	timestamp: string;
}

export interface Pod {
	_id: Id<'engagementPods'>;
	_creationTime: number;
	name: string;
	platform: string;
	userId: string;
	members: PodMember[];
	isActive: boolean;
	createdAt: string;
	updatedAt: string;
	runCount?: number;
	lastStatus?: {
		status: string;
		message: string;
		timestamp: string;
	};
	scheduledStart?: {
		timestamp: string;
		timezone: string;
		recurring?: string;
		recurringDays?: string[];
		lastRun?: string;
	};
	mode?: 'broadcast' | 'swarm';
	brandVoice?: {
		id: string;
		profileId?: Id<'profiles'>;
	};
}
